import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, MessageSquare, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

interface Ticket {
  id: string;
  subject: string;
  category: string;
  status: string;
  description: string;
  admin_response: string | null;
  created_at: string;
}

interface TicketListProps {
  user: User;
  refreshKey: number;
}

const statusStyles: Record<string, string> = {
  open: 'bg-muted text-muted-foreground',
  'in-progress': 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
  resolved: 'bg-accent/10 text-accent border-accent/30',
};

export function TicketList({ user, refreshKey }: TicketListProps) {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTickets = async () => {
      const { data } = await supabase
        .from('support_tickets' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      setTickets((data as any as Ticket[]) || []);
      setIsLoading(false);
    };
    fetchTickets();
  }, [user.id, refreshKey]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
        No tickets yet. Submit one above if you need help.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h4 className="font-medium text-foreground text-sm">Your Tickets</h4>
      {tickets.map((ticket) => (
        <Card key={ticket.id} className="bg-card/30 border-border/40 p-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h5 className="font-medium text-foreground text-sm flex-1">{ticket.subject}</h5>
            <Badge variant="outline" className={`text-xs shrink-0 ${statusStyles[ticket.status] || ''}`}>
              {ticket.status}
            </Badge>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
            <span className="capitalize">{ticket.category}</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(ticket.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
          {ticket.admin_response && (
            <div className="mt-3 p-3 bg-accent/5 border border-accent/20 rounded-md">
              <p className="text-xs font-medium text-accent mb-1">Admin Response</p>
              <p className="text-sm text-muted-foreground">{ticket.admin_response}</p>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
